import { forwardRef, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtQty, lineState, STATE_LABEL, unitWord } from "@/lib/billsLanguage";
import { IngredientPicker } from "@/components/bills/IngredientPicker";
import { ConversionQuestion } from "@/components/bills/ConversionQuestion";
import { PackSuggestion } from "@/components/bills/PackSuggestion";
import { SourceEditor } from "@/components/bills/SourceEditor";

const linkBtn = "inline-flex items-center gap-1 min-h-[44px] px-1 text-[14px] font-medium text-[var(--action)] underline-offset-2 hover:underline";

const Resolved = ({ line, canEdit, onEdit }) => (
  <div className="mt-2 flex items-center justify-between gap-3" data-testid={`conversion-resolved-${line.line_id}`}>
    <p className="text-[15px] font-mono text-archival-primary">
      {fmtQty(line.raw_quantity)} {line.raw_unit} → <span data-testid={`conversion-base-${line.line_id}`}>{fmtQty(line.base_quantity)} {line.base_uom}</span>
      <CheckCircle2 size={16} className="inline ml-1.5 -mt-0.5 text-[var(--action)]" aria-label="ready" />
    </p>
    {canEdit && <button type="button" onClick={onEdit} className={linkBtn} data-testid={`conversion-change-${line.line_id}`}>Change</button>}
  </div>
);

const Learned = ({ tid, children }) => (
  <p className="text-[12px] text-archival-muted" data-testid={tid}>{children}</p>
);

function ConversionBlock({ line, state, canEdit, busy, onConversion }) {
  const [editing, setEditing] = useState(false);
  useEffect(() => setEditing(false), [line.evidence_version, line.decision]);
  const sugg = line.resolution?.suggestions?.[0];
  const perUnit = line.base_quantity && line.raw_quantity ? line.base_quantity / line.raw_quantity : null;
  const submit = (body) => onConversion({ ...body, reason: editing ? "operator_change" : undefined });
  const mathOnly = line.decision === "AUTO_DETERMINISTIC" || line.decision === "NO_CONVERSION_NEEDED";
  if (state === "READY" && !editing) return (
    <>
      <Resolved line={line} canEdit={canEdit && !mathOnly} onEdit={() => setEditing(true)} />
      {line.decision === "AUTO_LEARNED_HIGH_CONFIDENCE" && <Learned tid={`conversion-learned-${line.line_id}`}>Converted automatically · learned from your previous confirmations</Learned>}
    </>
  );
  if (!canEdit) return null;
  if (state === "CHECK_PACK" && sugg && !editing)
    return <PackSuggestion line={line} suggestion={sugg} busy={busy} onUse={submit} onChange={() => setEditing(true)} />;
  return (
    <ConversionQuestion key={line.evidence_version} line={line} busy={busy} onSubmit={submit}
      initial={editing ? perUnit ?? sugg?.multiplier : undefined}
      onCancel={editing ? () => setEditing(false) : undefined} />
  );
}

export const LineCard = forwardRef(function LineCard({ line, gate, canEdit, busy, error, highlight, priceNote, onIngredient, onConversion, onSource }, ref) {
  const state = lineState(line, gate);
  const [mode, setMode] = useState(null);
  useEffect(() => setMode(null), [line.evidence_version, line.resolved_ingredient_id]);
  const needsIng = state === "NEEDS_INGREDIENT";
  return (
    <article ref={ref} tabIndex={-1} data-testid={`line-card-${line.line_id}`} data-state={state}
      aria-label={`${line.item_name}: ${STATE_LABEL[state]}`}
      className={cn("rounded-md border bg-white p-4 transition-shadow focus:outline-none",
        state === "READY" ? "border-[#E4E0D8]" : "border-amber-300 border-l-4",
        highlight && "ring-2 ring-amber-400 shadow-sm")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[16px] font-semibold text-archival-primary break-words" data-testid={`line-name-${line.line_id}`}>{line.item_name}</h3>
          <p className="text-[14px] font-mono text-archival-secondary" data-testid={`line-qty-${line.line_id}`}>{fmtQty(line.raw_quantity)} {line.raw_unit}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[15px] font-mono text-archival-primary">{fmtMoney(line.total_value)}</p>
          {canEdit && mode !== "source" && (
            <button type="button" onClick={() => setMode("source")} className={linkBtn} data-testid={`line-edit-source-${line.line_id}`}>
              <Pencil size={13} aria-hidden />Edit
            </button>
          )}
        </div>
      </div>
      {state !== "READY" && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-amber-800" data-testid={`line-state-${line.line_id}`}>
          <AlertCircle size={15} aria-hidden />{STATE_LABEL[state]}
        </p>
      )}
      {mode === "source" && <SourceEditor line={line} busy={busy} onCancel={() => setMode(null)} onSave={onSource} />}
      <div className="mt-3 border-t border-[#E4E0D8] pt-3">
        {!needsIng && mode !== "ingredient" && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-[14px] text-archival-secondary">Mapped to <span className="font-semibold text-archival-primary" data-testid={`line-ingredient-${line.line_id}`}>{line.ingredient_name}</span>
              <span className="text-[12px] text-archival-muted"> · in {unitWord(line.target_base_uom)}</span></p>
            {canEdit && <button type="button" onClick={() => setMode("ingredient")} className={linkBtn} data-testid={`line-change-ingredient-${line.line_id}`}>Change</button>}
          </div>
        )}
        {!needsIng && mode !== "ingredient" && line.mapping_source === "LEARNED_AUTO" && <Learned tid={`mapping-learned-${line.line_id}`}>Mapped automatically · learned from previous bills</Learned>}
        {needsIng && canEdit && line.mapping_suggestion && (
          <div className="mb-2 flex items-center justify-between gap-3 rounded-md bg-archival-soft px-3 py-2" data-testid={`mapping-suggestion-${line.line_id}`}>
            <p className="text-[14px] text-archival-primary">We usually map this to <span className="font-semibold">{line.mapping_suggestion.ingredient_name}</span></p>
            <button type="button" disabled={busy} onClick={() => onIngredient(line.mapping_suggestion.ingredient_id)} className={linkBtn} data-testid={`mapping-suggestion-use-${line.line_id}`}>Use</button>
          </div>
        )}
        {needsIng && !canEdit && <p className="text-[14px] text-archival-secondary">Ingredient needed</p>}
        {canEdit && (needsIng || mode === "ingredient") && (
          <IngredientPicker lineId={line.line_id} busy={busy} onPick={onIngredient} onCancel={needsIng ? undefined : () => setMode(null)} />
        )}
        {!needsIng && mode !== "ingredient" && (
          <ConversionBlock line={line} state={state} canEdit={canEdit} busy={busy} onConversion={onConversion} />
        )}
      </div>
      {priceNote && (
        <p className="mt-2 text-[12px] text-archival-secondary" data-testid={`line-price-note-${line.line_id}`}>
          <span className="font-medium">
            {priceNote.pct > 0 ? `${priceNote.pct}% higher` : `${Math.abs(priceNote.pct)}% lower`}
          </span>{" "}
          than your last comparable purchase ({priceNote.unit})
          {priceNote.date ? ` on ${priceNote.date}` : ""}
          {priceNote.billId ? (
            <>
              {" · "}
              <a href={`/bills/detail/${priceNote.billId}`} className="underline"
                data-testid={`line-price-note-link-${line.line_id}`}>
                {priceNote.billNumber ? `bill ${priceNote.billNumber}` : "view that bill"}
              </a>
            </>
          ) : null}
        </p>
      )}
      {error && <p role="alert" className="mt-2 text-[13px] text-red-700" data-testid={`line-error-${line.line_id}`}>{error}</p>}
    </article>
  );
});
